import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {
  getInterviewSchedules,
  getCandidateFeedbacks,
} from 'src/action-creators/crm';
import {useInflight} from 'src/hooks/redux';
import useScrollLoader from 'src/hooks/useScrollLoader';
import {useSelector, useDispatch} from 'react-redux';
import {
  selectCompletedCandidateFeedbacks,
  selectCompletedInterviewSchedules,
} from 'src/selectors/crm';
import {
  SimpleButtonDropdown,
  EmptyState,
  type MenuOption,
} from '@spaced-out/ui-design-system/lib/components';
import {CompletedCard} from 'src/components/crm/jobs/job-info/overview/feedbacks/cards/completed-card';
import {useAsyncDependency} from 'src/hooks/useAsyncDependency';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import css from './completed-feedback-tab.css';


type FeedbackType = 'interview' | 'applicant';

type CompletedFeedbackTabProps = {
  jobId?: string;
  from?: string;
};

export const CompletedFeedbackTab = ({
  jobId,
  from,
}: CompletedFeedbackTabProps) => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();
  const [feedbackType, setFeedbackType] =
    React.useState<FeedbackType>('interview');
  const [pagination, setPagination] = React.useState({
    interview: {page: 0, hasMore: true},
    applicant: {page: 0, hasMore: true},
  });
  const [isFetchingMore, setIsFetchingMore] = React.useState(false);
  const [feedbacks, setFeedbacks] = React.useState({
    interview: [],
    applicant: [],
  });

  const candidateFeedbacks = useSelector(selectCompletedCandidateFeedbacks);
  const interviewFeedbacks = useSelector(selectCompletedInterviewSchedules);

  const isCandidateFeedbacksLoading = useInflight(
    getCandidateFeedbacks('COMPLETED', jobId),
  );
  const isInterviewFeedbacksLoading = useInflight(
    getInterviewSchedules('COMPLETED', jobId),
  );

  const isInitialLoading = useAsyncDependency(
    () =>
      Promise.all([
        dispatch(getCandidateFeedbacks('COMPLETED', jobId)),
        dispatch(getInterviewSchedules('COMPLETED', jobId)),
      ]),
    [],
  );

  // Process and deduplicate feedbacks
  // @ts-expect-error - TS7006 - Parameter 'prevFeedbacks' implicitly has an 'any' type.
  const processFeedbacks = (prevFeedbacks, newContent: any) => {
    if (!newContent) {
      return prevFeedbacks;
    }

    const combined = [...prevFeedbacks, ...newContent];
    return combined.reduce<Array<any>>((acc, item) => {
      if (
        item?.interviewReviews &&
        !acc.find((existing) => existing.id === item.id)
      ) {
        acc.push(item);
      }
      return acc;
    }, []);
  };

  // Update feedbacks when data loads
  React.useEffect(() => {
    if (isInitialLoading) {
      return;
    }

    // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'INTERVIEW_SCHEDULE[]'.
    if (interviewFeedbacks?.content) {
      setFeedbacks((prev) => ({
        ...prev,
        // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'INTERVIEW_SCHEDULE[]'.
        interview: processFeedbacks(prev.interview, interviewFeedbacks.content),
      }));

      // @ts-expect-error - TS2339 - Property 'last' does not exist on type 'INTERVIEW_SCHEDULE[]'.
      if (interviewFeedbacks.last) {
        setPagination((prev) => ({
          ...prev,
          interview: {...prev.interview, hasMore: false},
        }));
      }
    }

    // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'CANDIDATE_FEEDBACK[]'.
    if (candidateFeedbacks?.content) {
      setFeedbacks((prev) => ({
        ...prev,
        // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'CANDIDATE_FEEDBACK[]'.
        applicant: processFeedbacks(prev.applicant, candidateFeedbacks.content),
      }));

      // @ts-expect-error - TS2339 - Property 'last' does not exist on type 'CANDIDATE_FEEDBACK[]'.
      if (candidateFeedbacks.last) {
        setPagination((prev) => ({
          ...prev,
          applicant: {...prev.applicant, hasMore: false},
        }));
      }
    }
  }, [isInitialLoading, interviewFeedbacks, candidateFeedbacks]);

  const loadMoreFeedbacks = React.useCallback(() => {
    if (isFetchingMore || !pagination[feedbackType].hasMore) {
      return;
    }

    setIsFetchingMore(true);
    const nextPage = pagination[feedbackType].page + 1;

    const fetchAction =
      feedbackType === 'interview'
        ? getInterviewSchedules('COMPLETED', jobId, nextPage)
        : getCandidateFeedbacks('COMPLETED', jobId, nextPage);

    dispatch(fetchAction)
      // @ts-expect-error - TS2339 - Property 'then' does not exist on type 'ThunkAction<void>'. | TS7006 - Parameter 'result' implicitly has an 'any' type.
      .then((result) => result.payload || result)
      .then((response) => {
        setPagination((prev) => ({
          ...prev,
          [feedbackType]: {
            page: nextPage,
            hasMore: !response?.last,
          },
        }));

        if (response?.content) {
          setFeedbacks((prev) => ({
            ...prev,
            [feedbackType]: processFeedbacks(
              prev[feedbackType],
              response.content,
            ),
          }));
        }

        setIsFetchingMore(false);
      })
      .catch((error) => {
        console.error('Error loading more feedbacks:', error);
        setIsFetchingMore(false);
      });
  }, [dispatch, jobId, feedbackType, isFetchingMore, pagination]);

  const [containerRef, loaderRef, handleScroll] = useScrollLoader({
    loading: isFetchingMore,
    done: !pagination[feedbackType].hasMore,
    onLoadMore: loadMoreFeedbacks,
  });

  const refreshFeedbacks = () => {
    dispatch(getCandidateFeedbacks('COMPLETED', jobId));
    dispatch(getInterviewSchedules('COMPLETED', jobId));
  };

  const handleFeedbackTypeChange = (option: MenuOption) => {
    // @ts-expect-error TS2345
    setFeedbackType(option.key);
  };

  const renderFeedbackItems = () => {
    const currentFeedbacks = feedbacks[feedbackType].filter(
      // @ts-expect-error - TS2339 - Property 'interviewReviews' does not exist on type 'never'.
      (item) => item?.interviewReviews,
    );

    if (currentFeedbacks.length === 0) {
      return (
        <EmptyState
          title={t('No_Feedbacks_Yet', 'No Feedbacks Yet')}
          description={`${t(
            'All_clear_for_now_Keep_an_eye_out',
            'All clear for now. Keep an eye out',
          )}!`}
          imageVariant="file"
        />
      );
    }

    return (
      <div
        className={css.feedbackList}
        // @ts-expect-error - TS2322 - Type 'ReactRef<HTMLElement>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
        ref={containerRef}
        onScroll={handleScroll}
      >
        {currentFeedbacks.map((feedback) =>
          // @ts-expect-error - TS2339 - Property 'interviewReviews' does not exist on type 'never'. | TS7006 - Parameter 'review' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type.
          feedback.interviewReviews.map((review, index) => (
            <CompletedCard
              // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
              key={`${feedback.id}-${index}`}
              completedData={feedback}
              review={review}
              type={feedbackType}
              isSmall={true}
              onUpdateFeedbacks={refreshFeedbacks}
              from={from}
            />
          )),
        )}

        {pagination[feedbackType].hasMore && (
          // @ts-expect-error - TS2322 - Type 'ReactRef<HTMLElement>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
          <div ref={loaderRef} className={css.loadingMoreContainer}>
            {isFetchingMore && <CircularLoader size="small" />}
          </div>
        )}
      </div>
    );
  };

  const feedbackTypeOptions = [
    {key: 'interview', label: t('Interview_Feedback', 'Interview Feedback')},
    {key: 'applicant', label: t('Applicant_Review', 'Applicant Review')},
  ];

  const isLoading =
    isInitialLoading ||
    (isCandidateFeedbacksLoading && !isFetchingMore) ||
    (isInterviewFeedbacksLoading && !isFetchingMore);

  return (
    <div className={css.completedTab}>
      {isLoading ? (
        <CircularLoader className={css.loader} size="large" />
      ) : (
        <>
          <div>
            <SimpleButtonDropdown
              size="small"
              type="ghost"
              children={
                feedbackTypeOptions.find(
                  (option) => option.key === feedbackType,
                )?.label
              }
              anchorPosition="bottom-start"
              onOptionSelect={handleFeedbackTypeChange}
              options={feedbackTypeOptions}
              selectedKeys={[feedbackType]}
              // @ts-expect-error - TS2769 - No overload matches this call.
              selectedOption={feedbackType}
            />
          </div>
          {renderFeedbackItems()}
        </>
      )}
    </div>
  );
};
